/**
 * Schedules forced token refreshes at specific future timestamps.
 *
 * Multiple timestamps can be registered; all are persisted in localStorage as a
 * sorted JSON array so they survive page reloads, navigation, and browser
 * restarts. Multi-tab coordination is handled via the storage event: all open
 * tabs detect the schedule, but only the last tab to attempt the lock at
 * fire-time calls the API, while the Hub notification from that refresh
 * propagates the new tokens to every other tab.
 */

import { Logger } from './log';

const log = new Logger({ logger: 'scheduledTokenRefresh' });

const STORAGE_KEY = 'dst_scheduled_token_refresh';
const LOCK_KEY = 'dst_scheduled_token_refresh_lock';

// 50 ms window for concurrent tabs' timer callbacks to race and write their lock id.
// The last writer wins; others observe a different id and stand down.
const LOCK_SETTLE_MS = 50;

let pendingRefreshFn: (() => Promise<void>) | null = null;
let timerId: ReturnType<typeof setTimeout> | null = null;

function clearTimer() {
	if (timerId !== null) {
		clearTimeout(timerId);
		timerId = null;
	}
}

function generateId() {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function readTimestamps(): number[] {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === null) return [];
		const parsed: unknown = JSON.parse(stored);
		if (!Array.isArray(parsed)) return [];
		return (parsed as unknown[])
			.filter((v): v is number => typeof v === 'number')
			.sort((a, b) => a - b);
	} catch {
		return [];
	}
}

function writeTimestamps(timestamps: number[]) {
	if (timestamps.length === 0) {
		localStorage.removeItem(STORAGE_KEY);
	} else {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(timestamps));
	}
}

/**
 * Executes a refresh for the given firing timestamp.
 * Removes that timestamp (and any older ones) from storage after winning the lock,
 * then re-arms for the next remaining timestamp, if any.
 */
function executeRefresh(forTimestamp: number) {
	clearTimer();

	if (!pendingRefreshFn) {
		log.warn('executeRefresh called before initialize – no refresh function available');
		return;
	}

	const lockId = generateId();
	log.debug(`Attempting to acquire refresh lock '${lockId}'`);
	localStorage.setItem(LOCK_KEY, lockId);

	// After the settle window, check whether we are still the last writer.
	setTimeout(() => {
		if (localStorage.getItem(LOCK_KEY) !== lockId) {
			log.debug('Refresh lock not acquired – another tab is handling the refresh');
			// The winning tab will rewrite STORAGE_KEY (removing the fired timestamp),
			// which triggers our onStorageEvent to re-arm for the next one.
			return;
		}

		log.info('Refresh lock acquired – executing scheduled token refresh');
		// Remove the fired timestamp and anything older (all <= forTimestamp).
		const remaining = readTimestamps().filter((t) => t > forTimestamp);
		writeTimestamps(remaining);
		localStorage.removeItem(LOCK_KEY);

		void pendingRefreshFn?.().finally(() => {
			// Own writes don't trigger the storage event, so re-arm manually for
			// any remaining timestamps.
			const next = readTimestamps();
			const nextFirst = next[0];
			if (nextFirst !== undefined) {
				setupTimer(nextFirst);
			}
		});
	}, LOCK_SETTLE_MS);
}

function setupTimer(timestamp: number) {
	clearTimer();
	const delay = Math.max(0, timestamp - Date.now());
	log.debug(`Token refresh scheduled in ${delay} ms (at ${new Date(timestamp).toISOString()})`);
	timerId = setTimeout(() => {
		executeRefresh(timestamp);
	}, delay);
}

function onStorageEvent(event: StorageEvent) {
	if (event.key !== STORAGE_KEY) return;

	if (event.newValue === null) {
		// All timestamps were consumed by another tab.
		log.debug('All scheduled refreshes were claimed by another tab – cancelling local timer');
		clearTimer();
		return;
	}

	try {
		const parsed: unknown = JSON.parse(event.newValue);
		if (Array.isArray(parsed)) {
			const timestamps = (parsed as unknown[])
				.filter((v): v is number => typeof v === 'number')
				.sort((a, b) => a - b);
			const first = timestamps[0];
			if (first !== undefined) {
				log.debug(
					`Received updated schedule from another tab – next refresh at ${new Date(first).toISOString()}`,
				);
				setupTimer(first);
				return;
			}
		}
	} catch {
		// Malformed value – treat as empty.
	}
	clearTimer();
}

/**
 * Initialises the module. Must be called once during page load before
 * {@link scheduleRefresh} is used.
 *
 * Reads any previously stored schedule from localStorage and resumes the timer,
 * so refreshes scheduled in a previous session (or another tab) are not lost.
 *
 * @param refreshFn Async function that performs the actual forced token refresh.
 */
function initialize(refreshFn: () => Promise<void>) {
	pendingRefreshFn = refreshFn;

	window.addEventListener('storage', onStorageEvent);

	const timestamps = readTimestamps();
	const earliest = timestamps[0];
	if (earliest === undefined) return;

	if (earliest <= Date.now()) {
		// Overdue from a previous session – execute immediately via the lock so
		// that multiple tabs loading at the same time don't all call the API.
		log.debug('Found overdue scheduled refresh – executing immediately');
		executeRefresh(earliest);
	} else {
		setupTimer(earliest);
	}
}

/**
 * Schedules a forced token refresh to happen at the given Unix timestamp (ms).
 *
 * Multiple calls accumulate: each timestamp is added to the list independently.
 * The schedule persists in localStorage across page reloads and browser restarts.
 * All tabs that have called {@link initialize} will detect the schedule and set up
 * local timers so that each refresh happens even if the scheduling tab is closed.
 *
 * @param timestamp Unix timestamp in milliseconds when the refresh should happen.
 */
function scheduleRefresh(timestamp: number) {
	log.info(`Scheduling token refresh at ${new Date(timestamp).toISOString()}`);
	const current = readTimestamps();
	if (current.includes(timestamp)) {
		log.debug('Timestamp already scheduled – skipping duplicate');
		return;
	}

	const updated = [...current, timestamp].sort((a, b) => a - b);
	writeTimestamps(updated);

	// Only re-arm the timer if the new timestamp is the new earliest – otherwise
	// the existing timer is already set for something sooner.
	if (updated[0] === timestamp) {
		setupTimer(timestamp);
	}
}

export { initialize, scheduleRefresh };
